import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { LoggerService } from './logger.service';

export enum AgentLockoutReason {
  None = 'NONE',
  NoLicense = 'NO_LICENSE',
  NoCreditsRemaining = 'NO_CREDITS_REMAINING',
  CreditsExhaustedAfterCall = 'CREDITS_EXHAUSTED_AFTER_CALL',
  InvalidRegionConfiguration = 'INVALID_REGION_CONFIGURATION',
  ConsumptionSubscriptionUnresolved = 'CONSUMPTION_SUBSCRIPTION_UNRESOLVED'
}

@Injectable({
  providedIn: 'root'
})
export class AgentLockoutService {
  private readonly LOCKOUT_REASON_STORAGE_KEY = 'AgentLockoutReason';
  private lockoutInProgress = false;

  constructor(private apiService: ApiService, private router: Router, private loggerService: LoggerService) {}

  async lockout(reason: AgentLockoutReason, logoutReason?: string): Promise<void> {
    if (this.lockoutInProgress) {
      this.loggerService.logger.logDebug(`AgentLockoutService.lockout ignored because lockout is already in progress. reason=${reason}`);
      return;
    }

    this.lockoutInProgress = true;
    this.setStoredReason(reason);

    try {
      await this.apiService.logout(logoutReason || `Agent lockout triggered. reason=${reason}`);
    } catch (error) {
      this.loggerService.logger.logError(`AgentLockoutService.lockout logout failed: ${JSON.stringify(error)}`);
    }

    try {
      await this.router.navigate(['/error'], { queryParams: { reason } });
    } catch (error) {
      this.loggerService.logger.logError(`AgentLockoutService.lockout navigation failed: ${JSON.stringify(error)}`);
    } finally {
      this.lockoutInProgress = false;
    }
  }

  getStoredReason(): AgentLockoutReason | null {
    const raw = sessionStorage.getItem(this.LOCKOUT_REASON_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    if (Object.values(AgentLockoutReason).includes(raw as AgentLockoutReason)) {
      return raw as AgentLockoutReason;
    }

    return null;
  }

  setStoredReason(reason: AgentLockoutReason): void {
    sessionStorage.setItem(this.LOCKOUT_REASON_STORAGE_KEY, reason);
  }

  clearStoredReason(): void {
    sessionStorage.removeItem(this.LOCKOUT_REASON_STORAGE_KEY);
  }
}
